(() => {
  if (customElements.get('currents-glow-board')) return;

  class CurrentsGlowBoard extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.observers = [];
      this.frames = new Set();
      this.form = this.querySelector('[data-glow-form]');
      this.quantityInput = this.querySelector('[data-glow-quantity]');
      this.errorOutput = this.querySelector('[data-glow-error]');
      this.submitButton = this.querySelector('[data-glow-submit]');
      this.variants = [];
      this.busy = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      try {
        this.variants = JSON.parse(this.querySelector('[data-glow-variants]')?.textContent || '[]');
        this.money = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.dataset.currency || window.Shopify?.currency?.active || 'USD'
        });
      } catch {
        this.controller.abort();
        this.controller = null;
        return;
      }

      this.addEventListener('click', (event) => this.handleClick(event), { signal: this.signal });
      this.form?.addEventListener('change', (event) => {
        if (event.target.matches('[name="id"]')) this.selectVariant(event.target.value, true);
        if (event.target === this.quantityInput) this.updateQuantity(true);
      }, { signal: this.signal });
      this.quantityInput?.addEventListener('input', () => this.updateQuantity(false), { signal: this.signal });
      this.form?.addEventListener('submit', (event) => this.addToCart(event), { signal: this.signal });
      window.addEventListener('popstate', () => this.selectInitialVariant(), { signal: this.signal });
      this.selectInitialVariant();
      this.setupTrack('gallery', '[data-glow-gallery]', '[data-glow-slide]');
      this.setupTrack('video', '[data-glow-videos]', '[data-video-card]');
      this.setupVideos();
      this.setupSticky();
      this.dataset.ready = 'true';
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observers?.forEach((observer) => observer.disconnect());
      this.frames?.forEach((frame) => cancelAnimationFrame(frame));
      this.querySelectorAll('video').forEach((video) => video.pause());
    }

    selectInitialVariant() {
      const requested = new URL(window.location.href).searchParams.get('variant');
      const selected = this.variants.find((variant) => String(variant.id) === requested)
        || this.variants.find((variant) => String(variant.id) === this.form?.querySelector('[name="id"]:checked')?.value)
        || this.variants.find((variant) => variant.available)
        || this.variants[0];
      if (selected) this.selectVariant(selected.id, false);
    }

    selectVariant(id, updateUrl) {
      const variant = this.variants.find((item) => String(item.id) === String(id));
      if (!variant) return;
      this.variant = variant;
      this.form?.querySelectorAll('[name="id"]').forEach((radio) => {
        radio.checked = String(radio.value) === String(variant.id);
      });
      this.dataset.selectedVariant = variant.id;
      this.setText('[data-glow-price]', variant.formatted_price);
      this.setText('[data-glow-sticky-size]', variant.title);
      this.querySelectorAll('[data-glow-compare]').forEach((element) => {
        element.textContent = variant.formatted_compare_at_price || '';
        element.hidden = !(Number(variant.compare_at_price) > Number(variant.price));
      });
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url);
      }
      this.showError('');
      this.updateQuantity(false);
      this.updateSubmit();
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach((element) => { element.textContent = value || ''; });
    }

    getQuantity() {
      const minimum = Math.max(1, Number(this.quantityInput?.min) || 1);
      const maximum = this.quantityInput?.max ? Number(this.quantityInput.max) : Infinity;
      const value = Number(this.quantityInput?.value);
      return Math.min(maximum, Math.max(minimum, Number.isFinite(value) ? Math.floor(value) : minimum));
    }

    updateQuantity(normalize) {
      const quantity = this.getQuantity();
      if (normalize && this.quantityInput) this.quantityInput.value = quantity;
      if (this.variant) {
        const total = this.money.format((Number(this.variant.price) * quantity) / 100);
        this.setText('[data-glow-total]', total);
        this.setText('[data-glow-sticky-price]', total);
      }
      this.querySelectorAll('[data-quantity-step]').forEach((button) => {
        const step = Number(button.dataset.quantityStep);
        const minimum = Math.max(1, Number(this.quantityInput?.min) || 1);
        const maximum = this.quantityInput?.max ? Number(this.quantityInput.max) : Infinity;
        button.disabled = this.busy || (step < 0 && quantity <= minimum) || (step > 0 && quantity >= maximum);
      });
    }

    updateSubmit() {
      if (this.submitButton) {
        this.submitButton.disabled = this.busy || !this.variant?.available;
        this.submitButton.setAttribute('aria-busy', String(this.busy));
      }
      const label = this.busy ? this.dataset.addingLabel : this.variant?.available ? this.dataset.addLabel : this.dataset.soldOutLabel;
      this.setText('[data-glow-submit-label]', label);
    }

    handleClick(event) {
      const target = event.target.closest('button, a');
      if (!target || !this.contains(target)) return;
      if (target.hasAttribute('data-quantity-step')) {
        event.preventDefault();
        if (this.busy || !this.quantityInput) return;
        this.quantityInput.value = this.getQuantity() + Number(target.dataset.quantityStep);
        this.updateQuantity(true);
      }
      if (target.hasAttribute('data-gallery-index')) {
        event.preventDefault();
        this.scrollTrack('gallery', Number(target.dataset.galleryIndex));
      }
      if (target.hasAttribute('data-video-play')) {
        event.preventDefault();
        const video = target.closest('[data-video-card]')?.querySelector('video');
        if (video) {
          video.controls = true;
          const restore = () => { target.hidden = false; };
          try {
            const playback = video.play();
            playback?.catch(restore);
          } catch {
            restore();
          }
        }
      }
      for (const kind of ['gallery', 'video']) {
        const direction = target.getAttribute(`data-${kind}-direction`);
        if (direction !== null) {
          event.preventDefault();
          const state = this[`${kind}Track`];
          if (state) this.scrollTrack(kind, state.index + Number(direction));
        }
      }
      if (target.hasAttribute('data-glow-jump')) {
        event.preventDefault();
        const selected = this.form?.querySelector('[name="id"]:checked');
        const destination = this.querySelector('[data-glow-options]') || selected?.closest('fieldset') || this.querySelector('[data-glow-buy]');
        destination?.scrollIntoView({ behavior: this.reducedMotion.matches ? 'instant' : 'smooth', block: 'center' });
        selected?.focus({ preventScroll: true });
      }
      if (target.hasAttribute('data-glow-cart')) {
        event.preventDefault();
        const drawer = document.querySelector('cart-drawer');
        if (typeof drawer?.open === 'function') drawer.open(target);
        else window.location.assign(window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
      }
    }

    setupTrack(kind, trackSelector, itemSelector) {
      const track = this.querySelector(trackSelector);
      if (!track) return;
      const items = Array.from(track.querySelectorAll(itemSelector));
      if (!items.length) return;
      const state = { track, items, index: 0, frame: null };
      this[`${kind}Track`] = state;
      const schedule = () => {
        if (state.frame !== null) return;
        state.frame = requestAnimationFrame(() => {
          this.frames.delete(state.frame);
          state.frame = null;
          this.updateTrack(kind);
        });
        this.frames.add(state.frame);
      };
      track.addEventListener('scroll', schedule, { passive: true, signal: this.signal });
      track.addEventListener('keydown', (event) => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.scrollTrack(kind, state.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, { signal: this.signal });
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(schedule);
        observer.observe(track);
        this.observers.push(observer);
      } else {
        window.addEventListener('resize', schedule, { passive: true, signal: this.signal });
      }
      this.updateTrack(kind);
    }

    updateTrack(kind) {
      const state = this[`${kind}Track`];
      if (!state) return;
      const edge = state.track.getBoundingClientRect().left;
      state.index = state.items.reduce((closest, item, index) => {
        return Math.abs(item.getBoundingClientRect().left - edge) < Math.abs(state.items[closest].getBoundingClientRect().left - edge) ? index : closest;
      }, 0);
      this.setText(`[data-${kind}-counter]`, `${String(state.index + 1).padStart(2, '0')} / ${String(state.items.length).padStart(2, '0')}`);
      const atStart = state.track.scrollLeft <= 2;
      const atEnd = state.track.scrollLeft >= state.track.scrollWidth - state.track.clientWidth - 2;
      this.querySelectorAll(`[data-${kind}-direction]`).forEach((button) => {
        button.disabled = Number(button.getAttribute(`data-${kind}-direction`)) < 0 ? atStart : atEnd;
      });
      if (kind === 'gallery') {
        this.querySelectorAll('[data-gallery-index]').forEach((button) => {
          button.setAttribute('aria-current', String(Number(button.dataset.galleryIndex) === state.index));
        });
      }
    }

    scrollTrack(kind, index) {
      const state = this[`${kind}Track`];
      if (!state) return;
      const item = state.items[Math.max(0, Math.min(index, state.items.length - 1))];
      const left = item.getBoundingClientRect().left - state.track.getBoundingClientRect().left + state.track.scrollLeft;
      state.track.scrollTo({ left, behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
    }

    setupVideos() {
      const videos = Array.from(this.querySelectorAll('[data-glow-videos] video'));
      videos.forEach((video) => {
        const button = video.closest('[data-video-card]')?.querySelector('[data-video-play]');
        const updateOverlay = () => {
          if (button) button.hidden = !video.paused && !video.ended;
        };
        video.addEventListener('play', () => {
          videos.forEach((other) => { if (other !== video) other.pause(); });
          updateOverlay();
        }, { signal: this.signal });
        ['pause', 'ended', 'error'].forEach((event) => video.addEventListener(event, updateOverlay, { signal: this.signal }));
        updateOverlay();
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) videos.forEach((video) => video.pause());
      }, { signal: this.signal });
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.15) entry.target.pause();
          });
        }, { threshold: [0, 0.15] });
        videos.forEach((video) => observer.observe(video));
        this.observers.push(observer);
      }
    }

    setupSticky() {
      const sticky = this.querySelector('[data-glow-sticky]');
      const buy = this.querySelector('[data-glow-buy]');
      if (!sticky || !buy) return;
      const update = () => {
        const visible = buy.getBoundingClientRect().bottom < 0;
        sticky.hidden = !visible;
        sticky.inert = !visible;
        sticky.setAttribute('aria-hidden', String(!visible));
        sticky.classList.toggle('is-visible', visible);
      };
      update();
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(update, { threshold: 0 });
        observer.observe(buy);
        this.observers.push(observer);
      } else {
        window.addEventListener('scroll', update, { passive: true, signal: this.signal });
      }
    }

    setBusy(busy) {
      this.busy = busy;
      this.form?.setAttribute('aria-busy', String(busy));
      if (busy) {
        this.disabledState = Array.from(this.form?.querySelectorAll('input, button, select') || []).map((control) => [control, control.disabled]);
        this.disabledState.forEach(([control]) => { control.disabled = true; });
      } else {
        this.disabledState?.forEach(([control, disabled]) => { control.disabled = disabled; });
        this.disabledState = null;
      }
      this.updateQuantity(false);
      this.updateSubmit();
    }

    showError(message) {
      if (!this.errorOutput) return;
      this.errorOutput.textContent = message;
      this.errorOutput.hidden = !message;
    }

    async addToCart(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.busy || !this.variant?.available || !this.form?.reportValidity()) return;
      this.updateQuantity(true);
      this.showError('');
      const body = new FormData(this.form);
      body.set('id', this.variant.id);
      body.set('quantity', this.getQuantity());
      const drawer = document.querySelector('cart-drawer');
      const sections = typeof drawer?.getSectionsToRender === 'function' ? drawer.getSectionsToRender().map((section) => section.id) : ['cart-drawer', 'cart-icon-bubble'];
      body.set('sections', sections.join(','));
      body.set('sections_url', window.location.pathname);
      drawer?.setActiveElement?.(this.submitButton);
      this.setBusy(true);
      let added = false;
      try {
        const url = new URL(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add`, window.location.origin);
        if (!url.pathname.endsWith('.js')) url.pathname += '.js';
        const response = await fetch(url.href, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body, credentials: 'same-origin' });
        const result = await response.json();
        if (!response.ok || result.status) {
          const details = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : this.dataset.errorLabel;
          throw new Error(details);
        }
        added = true;
        if (!drawer || typeof drawer.renderContents !== 'function' || typeof drawer.open !== 'function') {
          window.location.assign(window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
          return;
        }
        if (sections.some((section) => !result.sections?.[section])) {
          const refreshUrl = new URL(window.location.href);
          refreshUrl.searchParams.set('sections', sections.join(','));
          const refresh = await fetch(refreshUrl.href, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
          if (!refresh.ok) throw new Error(this.dataset.errorLabel);
          result.sections = await refresh.json();
        }
        if (sections.some((section) => !result.sections?.[section])) throw new Error(this.dataset.errorLabel);
        drawer.classList.remove('is-empty');
        drawer.renderContents(result, true);
        drawer.open(this.submitButton);
        this.dispatchEvent(new CustomEvent('glow:cart-added', { bubbles: true, detail: { variantId: body.get('id'), quantity: Number(body.get('quantity')) } }));
      } catch (error) {
        if (added) window.location.assign(window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
        else this.showError(error instanceof TypeError ? this.dataset.errorLabel : error.message || this.dataset.errorLabel);
      } finally {
        this.setBusy(false);
      }
    }
  }

  customElements.define('currents-glow-board', CurrentsGlowBoard);
})();
